/* The things you would like to do once, rather than every week.
 *
 * A game you want to play. A book you want to read. The exhibition that closes
 * in March. None of these is a weekly quota, and forcing them into one is what
 * made the old screen feel like a budgeting app: "two hours of Zelda a week"
 * is not a sentence anybody has ever meant.
 *
 * So a possible activity is stored as an ordinary **task** under a not-work
 * category, and that is the whole design decision. A task already has an
 * estimate, a done flag, a place in the solver's queue and a screen that shows
 * where it landed. A second, half-built list of nearly-tasks would have all of
 * that again, worse, and the two would disagree within a week.
 *
 * What it adds on top is the one thing tasks do not say: these are for the
 * time that is *yours*, so they are only ever placed in the hours the planner
 * treats as not-work.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Hobby, Task } from '@shared/types';
import { get, post, patch, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { human, nearLabel } from '../../ui/time';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';

export default function Possible({ hobbies, today }: { hobbies: Hobby[]; today: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [typeId, setTypeId] = useState('');
  const [minutes, setMinutes] = useState('90');
  const [showDone, setShowDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tasks = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const r = await get<{ tasks: Task[] }>('/api/tasks');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your list.'));
      return r.tasks;
    }
  });

  const lifeTypes = new Set(hobbies.map(h => h.typeId));
  const mine = (tasks.data ?? []).filter(t => t.typeId && lifeTypes.has(t.typeId));
  const open = mine.filter(t => !t.done);
  const done = mine.filter(t => t.done);

  const add = useMutation({
    mutationFn: async () => {
      const r = await post('/api/tasks', {
        title: title.trim(),
        estMin: Math.max(15, Math.min(600, Number(minutes) || 90)),
        typeId: typeId || hobbies[0]?.typeId,
        /* `could`, not `should`: this is the pile the planner reaches for when
           there is room, and marking it urgent would let a book compete with a
           deadline. */
        urgency: 'could'
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that.'));
      return title.trim();
    },
    onSuccess: t => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      setTitle('');
      setError(null);
      toast.say(`“${t}” is on the list. Amon offers it a slot when there is one.`, 'good');
    },
    onError: (e: Error) => setError(e.message)
  });

  const tick = useMutation({
    mutationFn: async ({ task, isDone }: { task: Task; isDone: boolean }) => {
      const r = await patch(`/api/tasks/${task.id}`, { done: isDone });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not update that.'));
      return { task, isDone };
    },
    onSuccess: ({ task, isDone }) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['hobbies'] });
      toast.say(isDone ? `Done: ${task.title}. That was yours.` : `Back on the list: ${task.title}`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('What is it?'); return; }
    if (!hobbies.length) { setError('Add a hobby first — this list files things under one.'); return; }
    add.mutate();
  }

  return (
    <Section id="possible-title" icon="star" className="tone-green"
             title="Things you would like to do"
             meta={open.length ? `${open.length} waiting for a free evening` : 'once, not every week'}
             info={<>A game, a book, the exhibition that closes in March. Not a weekly quota — a
               thing to be offered a slot when there is one, and then finished. Amon only
               ever places these in hours it treats as yours.</>}>

      <form className="quickadd" onSubmit={submit}>
        <input value={title} maxLength={200} placeholder="Finish Outer Wilds"
               aria-label="What would you like to do"
               onChange={e => setTitle(e.target.value)} />
        <select value={typeId} onChange={e => setTypeId(e.target.value)}
                aria-label="Which hobby it belongs to">
          {hobbies.length === 0 && <option value="">— add a hobby first —</option>}
          {hobbies.map(h => <option key={h.typeId} value={h.typeId}>{h.name}</option>)}
        </select>
        <input type="number" min="15" max="600" step="15" value={minutes}
               aria-label="Minutes" onChange={e => setMinutes(e.target.value)} />
        <span className="muted small">min</span>
        <button className="btn btn-primary btn-sm" type="submit" disabled={add.isPending}>
          <Icon name="plus" size={16} />{add.isPending ? 'Adding…' : 'Add'}
        </button>
        {error && <p className="auth-err" role="alert">{error}</p>}
      </form>

      {open.length === 0 && !tasks.isPending && (
        <p className="muted small">
          Nothing on the list. This is the one place in the app where "I would like to,
          one day" is a legitimate entry.
        </p>
      )}

      <ul className="possible-list">
        {open.map(t => (
          <li key={t.id}>
            <label className="task-check">
              <input type="checkbox" checked={false}
                     aria-label={`Mark done: ${t.title}`}
                     onChange={() => tick.mutate({ task: t, isDone: true })} />
            </label>
            <div>
              <strong>{t.title}</strong>
              <span className="muted small">
                {' '}· {human(t.mins)}
                {t.placedAt
                  ? ` · ${nearLabel(t.placedAt.date, today) ?? t.placedAt.date} at ${t.placedAt.start}`
                  : ' · waiting for an evening'}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {done.length > 0 && (
        <>
          <button className="linkish" onClick={() => setShowDone(s => !s)}
                  aria-expanded={showDone}>
            {showDone ? 'Hide' : 'Show'} {done.length} you have done
          </button>
          {showDone && (
            <ul className="possible-list">
              {done.map(t => (
                <li key={t.id} className="is-done">
                  <label className="task-check">
                    <input type="checkbox" checked readOnly={false}
                           aria-label={`Put back on the list: ${t.title}`}
                           onChange={() => tick.mutate({ task: t, isDone: false })} />
                  </label>
                  <div><strong>{t.title}</strong></div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Section>
  );
}
